import { useState, useEffect } from "react";
import { Loader2, Plus, Trash2, Save, Shield, UserCog, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  logAction: (action: string, entityType: string, entityId: string, details?: any) => void;
  currentUserId: string | null;
}

interface StaffMember {
  id: string;
  user_id: string;
  can_access_restaurants: boolean;
  can_edit_platform_settings: boolean;
  can_manage_plans: boolean;
  can_manage_support_tickets: boolean;
  can_view_audit_logs: boolean;
  can_manage_affiliates: boolean;
  is_active: boolean;
  created_at: string;
  profile?: { full_name: string | null; email?: string };
}

const AdminStaffTab = ({ logAction, currentUserId }: Props) => {
  const { toast } = useToast();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const loadStaff = async () => {
    setLoading(true);
    // Get all users with 'support' role
    const { data: roles } = await supabase
      .from("user_roles")
      .select("user_id")
      .eq("role", "support" as any);

    if (!roles || roles.length === 0) {
      setStaff([]);
      setLoading(false);
      return;
    }

    const userIds = roles.map(r => r.user_id);

    // Get permissions
    const { data: perms } = await supabase
      .from("staff_permissions")
      .select("*")
      .in("user_id", userIds);

    // Get profiles
    const { data: profiles } = await supabase
      .from("profiles")
      .select("user_id, full_name")
      .in("user_id", userIds);

    const staffList: StaffMember[] = userIds.map(uid => {
      const perm = perms?.find(p => p.user_id === uid);
      const prof = profiles?.find(p => p.user_id === uid);
      return {
        id: perm?.id || uid,
        user_id: uid,
        can_access_restaurants: perm?.can_access_restaurants ?? false,
        can_edit_platform_settings: perm?.can_edit_platform_settings ?? false,
        can_manage_plans: perm?.can_manage_plans ?? false,
        can_manage_support_tickets: perm?.can_manage_support_tickets ?? true,
        can_view_audit_logs: perm?.can_view_audit_logs ?? false,
        can_manage_affiliates: perm?.can_manage_affiliates ?? false,
        is_active: perm?.is_active ?? true,
        created_at: perm?.created_at || '',
        profile: { full_name: prof?.full_name || null },
      };
    });

    setStaff(staffList);
    setLoading(false);
  };

  useEffect(() => { loadStaff(); }, []);

  const updatePermission = async (member: StaffMember, field: string, value: boolean) => {
    setSaving(member.user_id);

    // Check if permissions record exists
    const { data: existing } = await supabase
      .from("staff_permissions")
      .select("id")
      .eq("user_id", member.user_id)
      .maybeSingle();

    let error;
    if (existing) {
      ({ error } = await supabase
        .from("staff_permissions")
        .update({ [field]: value } as any)
        .eq("user_id", member.user_id));
    } else {
      ({ error } = await supabase
        .from("staff_permissions")
        .insert({ user_id: member.user_id, [field]: value, created_by: currentUserId } as any));
    }

    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      setStaff(prev => prev.map(s =>
        s.user_id === member.user_id ? { ...s, [field]: value } : s
      ));
      logAction("staff_permission_updated", "staff_permissions", member.user_id, { field, value });
      toast({ title: "Permissão atualizada!" });
    }
    setSaving(null);
  };

  const createStaffUser = async () => {
    if (!newEmail || !newName || !newPassword) {
      toast({ title: "Preencha todos os campos", variant: "destructive" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: "Senha deve ter no mínimo 6 caracteres", variant: "destructive" });
      return;
    }

    setCreating(true);

    // Create user via edge function
    const { data, error } = await supabase.functions.invoke("create-support-user", {
      body: { email: newEmail, password: newPassword, full_name: newName },
    });

    if (error || data?.error) {
      toast({ title: "Erro ao criar usuário", description: error?.message || data?.error, variant: "destructive" });
    } else {
      logAction("support_user_created", "user", data.user_id, { email: newEmail });
      toast({ title: "Usuário de suporte criado!" });
      setNewEmail("");
      setNewName("");
      setNewPassword("");
      setShowCreate(false);
      await loadStaff();
    }
    setCreating(false);
  };

  const deleteStaffUser = async (member: StaffMember) => {
    if (!confirm(`Tem certeza que deseja remover ${member.profile?.full_name || member.user_id} da equipe de suporte?`)) return;

    // Remove support role (keep the user account)
    await supabase.from("user_roles").delete().eq("user_id", member.user_id).eq("role", "support" as any);
    await supabase.from("staff_permissions").delete().eq("user_id", member.user_id);

    logAction("support_user_removed", "user", member.user_id);
    toast({ title: "Usuário removido da equipe de suporte" });
    await loadStaff();
  };

  const permissions = [
    { key: "can_access_restaurants", label: "Acessar contas de restaurantes", desc: "Pode visualizar e gerenciar restaurantes" },
    { key: "can_edit_platform_settings", label: "Editar configurações da plataforma", desc: "Pode alterar configurações gerais do site" },
    { key: "can_manage_plans", label: "Gerenciar planos", desc: "Pode criar, editar e desativar planos" },
    { key: "can_manage_support_tickets", label: "Gerenciar tickets de suporte", desc: "Pode responder e gerenciar conversas de suporte" },
    { key: "can_view_audit_logs", label: "Visualizar auditoria", desc: "Pode acessar logs de auditoria" },
    { key: "can_manage_affiliates", label: "Gerenciar afiliados", desc: "Pode gerenciar programa de afiliados" },
  ];

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-lg">Equipe de Suporte</h2>
          <p className="text-xs text-muted-foreground">Gerencie funcionários com permissões granulares. Apenas o Super Admin pode alterar credenciais do Mercado Pago.</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-gradient-primary text-primary-foreground">
              <Plus className="h-4 w-4 mr-2" />Novo Funcionário
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar Usuário de Suporte</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label className="text-sm">Nome completo</Label>
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="João da Silva" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">E-mail</Label>
                <Input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="joao@viamenu.com.br" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Senha</Label>
                <div className="relative">
                  <Input type={showPassword ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button onClick={createStaffUser} disabled={creating} className="w-full bg-gradient-primary text-primary-foreground">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Criar Funcionário
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {staff.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <UserCog className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Nenhum funcionário de suporte cadastrado.</p>
          <p className="text-xs mt-1">Clique em "Novo Funcionário" para adicionar.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {staff.map(member => (
            <div key={member.user_id} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <UserCog className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                      {member.profile?.full_name || "Sem nome"}
                      {member.is_active ? (
                        <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-500">Ativo</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] border-destructive/30 text-destructive">Inativo</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{member.user_id.substring(0, 8)}...</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 mr-2">
                    <Label className="text-xs text-muted-foreground">Ativo</Label>
                    <Switch
                      checked={member.is_active}
                      onCheckedChange={v => updatePermission(member, 'is_active', v)}
                      disabled={saving === member.user_id}
                    />
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => deleteStaffUser(member)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {permissions.map(perm => (
                  <div key={perm.key} className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div>
                      <div className="text-xs font-medium">{perm.label}</div>
                      <div className="text-[10px] text-muted-foreground">{perm.desc}</div>
                    </div>
                    <Switch
                      checked={(member as any)[perm.key]}
                      onCheckedChange={v => updatePermission(member, perm.key, v)}
                      disabled={saving === member.user_id}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <Shield className="h-3 w-3" />
                  <span>Credenciais do Mercado Pago: <strong className="text-destructive">Apenas Super Admin</strong></span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminStaffTab;
